import { WAITING_ON_A_PERSON } from '@realplc/types';
import type { ChatSession, Message } from '../types';
import { isRepairInProgress, shouldRenderGeneratedCode } from './messageTopic';

type PlanReviewResult = NonNullable<NonNullable<Message['planReview']>['reviewResult']>;
type PlanProcessHistory = NonNullable<Message['planProcessHistory']>;
type PlanReview = NonNullable<Message['planReview']>;
type PlanReviewApprovalSource = NonNullable<PlanReview['approvalSource']>;

function isTransientReplanResult(result: PlanReviewResult | undefined): boolean {
  return result === 'rejected' || result === 'regenerate_subtask';
}

function planReviewSignature(planReview: Message['planReview']): string {
  if (!planReview) return '';
  const subtaskIds = (planReview.subtasks || []).map(item => `${item.id}:${item.title}`).join('|');
  const subtaskDetails = (planReview.subtasks || [])
    .map(item => `${item.description}:${item.acceptanceCriteria?.join(',')}`)
    .join('|');
  return [
    planReview.channel,
    planReview.requirementSummary,
    planReview.architecture?.programStructure,
    subtaskIds,
    subtaskDetails,
    planReview.selfCheck?.coverageScore,
    planReview.selfCheck?.safetyCoverage,
  ].filter(Boolean).join('::');
}

function isSamePlanReview(left: Message['planReview'], right: Message['planReview']): boolean {
  const leftSignature = planReviewSignature(left);
  const rightSignature = planReviewSignature(right);
  return Boolean(leftSignature && rightSignature && leftSignature === rightSignature);
}

/**
 * The decision the page holds for a plan, laid over a copy of that plan that
 * has not heard of it. The proposals ticked with the decision are part of the
 * decision: left behind here, a card drawn from the merged copy showed none
 * ticked from the next stream chunk on.
 */
function mergeSubmittedReviewMetadata(
  planReview: PlanReview,
  reviewResult: PlanReviewResult,
  approvalSource?: PlanReviewApprovalSource,
  acceptedSignals?: PlanReview['acceptedSignals'],
): PlanReview {
  return {
    ...planReview,
    reviewResult,
    approvalSource: approvalSource || planReview.approvalSource,
    ...(Array.isArray(acceptedSignals) ? { acceptedSignals } : {}),
  };
}

function mergePlanProcessHistoryResults(
  target: Message['planProcessHistory'],
  submitted: Message['planProcessHistory'],
  submittedPlanReview?: PlanReview,
): Message['planProcessHistory'] {
  if (!submitted?.length && !submittedPlanReview?.reviewResult) return target;
  if (!target?.length) return submitted;

  let changed = false;
  const submittedById = new Map(
    (submitted || [])
      .filter(item => item.planReview?.reviewResult)
      .map(item => [item.id || String(item.sequence), item] as const),
  );

  const next: PlanProcessHistory = target.map((item) => {
    const submittedItem = submittedById.get(item.id || String(item.sequence));
    const submittedPlan = submittedItem?.planReview?.reviewResult
      ? submittedItem.planReview
      : submittedPlanReview?.reviewResult
        && !isTransientReplanResult(submittedPlanReview.reviewResult)
        && isSamePlanReview(item.planReview, submittedPlanReview)
          ? submittedPlanReview
          : undefined;
    const submittedResult = submittedPlan?.reviewResult;
    if (item.planReview?.reviewResult) {
      if (
        submittedPlan
        && submittedResult === item.planReview.reviewResult
        && submittedPlan.approvalSource
        && item.planReview.approvalSource !== submittedPlan.approvalSource
        && isSamePlanReview(item.planReview, submittedPlan)
      ) {
        changed = true;
        return {
          ...item,
          planReview: {
            ...item.planReview,
            approvalSource: submittedPlan.approvalSource,
          },
        };
      }
      return item;
    }
    if (
      !submittedPlan
      || !submittedResult
      || !item.planReview
      || !isSamePlanReview(item.planReview, submittedPlan)
    ) {
      return item;
    }
    changed = true;
    return {
      ...item,
      status: 'submitted',
      planReview: mergeSubmittedReviewMetadata(
        item.planReview,
        submittedResult,
        submittedPlan.approvalSource,
        submittedPlan.acceptedSignals,
      ),
    };
  });

  return changed ? next : target;
}

function findSubmittedHistoryPlanReview(
  planReview: Message['planReview'],
  history: Message['planProcessHistory'],
): PlanReview | undefined {
  if (!planReview || !history?.length) return undefined;
  return history
    .map(item => item.planReview)
    .find(item => item?.reviewResult && isSamePlanReview(planReview, item)) as PlanReview | undefined;
}

function planReviewIsPending(planReview: Message['planReview']): boolean {
  // timed_out is still pending — user hasn't confirmed yet, just released resources
  return Boolean(planReview?.subtasks?.length && (!planReview.reviewResult || planReview.reviewResult === 'timed_out'));
}

export function messageHasCompletedProjectSummary(
  message: Pick<Message, 'isStreaming' | 'offTopic' | 'parsed' | 'pipeline' | 'progressState' | 'repairAppendix'>,
): boolean {
  if (message.isStreaming || message.offTopic === true || message.progressState?.phase === 'failed') {
    return false;
  }
  if (isRepairInProgress(message)) {
    return false;
  }

  const hasSummary = Boolean(
    message.parsed?.summary?.trim()
    || message.parsed?.explanation?.trim()
    || message.repairAppendix?.summary?.trim(),
  );

  return hasSummary && shouldRenderGeneratedCode(message);
}

/** Check if any message in the session has tangible output
 *  (generated code, summary, explanation, or repair output).
 *  Unlike messageHasCompletedProjectSummary this does NOT require
 *  the message to be non-streaming or pass the PLC-code heuristic.
 *  A failed message that still delivered partial output counts. */
export function sessionHasCompletedOutput(
  messages: Array<Pick<Message, 'role' | 'parsed' | 'repairAppendix' | 'progressState'>>,
): boolean {
  return messages.some(m =>
    m.role === 'assistant'
    && (
      m.parsed?.code?.trim()
      || m.parsed?.summary?.trim()
      || m.parsed?.explanation?.trim()
      || m.repairAppendix?.summary?.trim()
      || m.repairAppendix?.finalCode?.trim()
    ),
  );
}

export function hasPendingPlanReview(message: Pick<Message, 'planReview' | 'planProcessHistory'>): boolean {
  const planReview = message.planReview;
  if (planReviewIsPending(planReview)) return true;
  return Boolean(message.planProcessHistory?.some(item => planReviewIsPending(item.planReview)));
  // Only pending while actively streaming or waiting; submitted/completed/failed → done
}

export function sessionHasAwaitingPlanReview(session: Pick<ChatSession, 'messages'>): boolean {
  const latestCompletedSummaryIndex = session.messages.reduce(
    (latestIndex, message, index) => messageHasCompletedProjectSummary(message) ? index : latestIndex,
    -1,
  );

  return session.messages.some((message, index) =>
    index > latestCompletedSummaryIndex
    && !messageHasCompletedProjectSummary(message)
    && hasPendingPlanReview(message)
  );
}

/**
 * The plan-review stage, moved on the instant the user answers it.
 *
 * The step the turn draws is read from the stage buffer alone, and nothing in
 * the browser used to write to it: approving the plan set `reviewResult` and
 * left the buffer saying `needs_approval`, so the amber "waiting for your
 * approval" card sat there unchanged for the whole round trip to the runtime
 * and back. Read from the page, that is a plan that was never submitted.
 *
 * The stage becomes *working*, not passed. A tick would assert the runtime had
 * accepted the approval, which is exactly what has not happened yet; that the
 * approval is submitted and being acted on is true and is all this claims. The
 * runtime's own `stage_done` settles it a moment later.
 *
 * The clock restarts at the approval because the minutes spent reading a plan
 * are not the run working — every other step's seconds are machine time, and
 * this one has to mean the same thing.
 */
export function advancePlanReviewOnApproval(
  buffers: Message['stageBuffers'],
  approvedAt: number,
): Message['stageBuffers'] {
  const planReview = buffers?.planReview;
  // Only a stage still waiting on the user is ours to move: a stale or repeated
  // click must not reopen one the runtime has already carried forward.
  if (!planReview?.status || !WAITING_ON_A_PERSON.includes(planReview.status)) return buffers;

  return {
    ...buffers,
    planReview: {
      ...planReview,
      status: 'streaming',
      done: false,
      // What the stage said while it waited was the question. A stored
      // conclusion outranks the one derived from the answer, so kept past the
      // click it went on asking: 2026-09-23 线上，批准后「计划审批」整轮写着
      // 「等待计划确认。」。
      conclusion: undefined,
      startedAt: approvedAt,
      endedAt: undefined,
      durationMs: undefined,
    },
  };
}

export function mergeSubmittedPlanReviewResults(
  messages: Message[],
  ...sources: Array<Message[] | undefined>
): Message[] {
  const submittedResults = new Map<string, PlanReview>();
  const submittedPlanHistories = new Map<string, Message['planProcessHistory']>();

  sources.forEach((source) => {
    source?.forEach((message) => {
      const reviewResult = message.planReview?.reviewResult;
      if (reviewResult) {
        submittedResults.set(message.id, message.planReview as PlanReview);
      }
      if (message.planProcessHistory?.some(item => item.planReview?.reviewResult)) {
        submittedPlanHistories.set(message.id, message.planProcessHistory);
      }
    });
  });

  if (submittedResults.size === 0 && submittedPlanHistories.size === 0) {
    return messages;
  }

  let changed = false;
  const nextMessages = messages.map((message) => {
    const submittedHistory = submittedPlanHistories.get(message.id);
    const submittedPlanReview = submittedResults.get(message.id)
      || findSubmittedHistoryPlanReview(message.planReview, submittedHistory);
    const reviewResult = submittedPlanReview?.reviewResult;
    const approvalSource = submittedPlanReview?.approvalSource;
    const mergedHistory = mergePlanProcessHistoryResults(message.planProcessHistory, submittedHistory, submittedPlanReview);
    const shouldMergeTopLevelReview = Boolean(
      reviewResult
      && message.planReview
      && (message.planReview.reviewResult || !isTransientReplanResult(reviewResult))
      && isSamePlanReview(message.planReview, submittedPlanReview)
      && (
        message.planReview.reviewResult !== reviewResult
        || (approvalSource && message.planReview.approvalSource !== approvalSource)
      ),
    );
    if (
      (
        !shouldMergeTopLevelReview
      )
      && mergedHistory === message.planProcessHistory
    ) {
      return message;
    }

    changed = true;
    const planReview = shouldMergeTopLevelReview
      && reviewResult
      && message.planReview
      ? mergeSubmittedReviewMetadata(
          message.planReview,
          reviewResult,
          approvalSource,
          submittedPlanReview?.acceptedSignals,
        )
      : message.planReview;

    return {
      ...message,
      planReview,
      planProcessHistory: mergedHistory,
    };
  });

  return changed ? nextMessages : messages;
}

export function mergeSessionSubmittedPlanReviewResults(
  session: ChatSession,
  ...sources: Array<ChatSession | undefined>
): ChatSession {
  const messages = mergeSubmittedPlanReviewResults(
    session.messages,
    ...sources.map(source => source?.messages),
  );

  return messages === session.messages
    ? session
    : {
        ...session,
        messages,
      };
}
